import {
  applyClipArrangement,
  clipArrangementSnapshot,
  cloneInstrument,
  clonePreset,
  DEFAULT_INSTRUMENT_SLOT_COUNT,
  defaultPresetForSlot,
  ensureClipTrackCount,
  instrumentsFromPresets,
  MAX_INSTRUMENT_SLOT_COUNT,
  normalizeClipScenes,
  normalizeFxNode,
  normalizeInstrument,
  normalizeSlotMixes,
  normalizeSlotFxStacks,
  parsePatternString,
  presetsFromInstruments,
  stringifyPattern,
  type ClipIR,
  type FxNode,
  type Instrument,
  type InstrumentSlot,
  type Scene,
  type SlotMix,
  type SynthPreset,
} from "../../../../src/lib/audio";
import { isSynthPreset, sanitizeBank, type NamedPreset } from "./presetBank";
import { createDefaultDrumBank, sanitizeDrumBank, type NamedDrumKit } from "./drumKitBank";
import { createDefaultWavetableBank, sanitizeWavetableBank, type NamedWavetablePreset } from "./wavetableBank";
import { createDefaultSamplerBank, sanitizeSamplerBank, type NamedSamplerPreset } from "./samplerBank";
import { inferTrackCount, normalizeMuteSoloFlags } from "./muteSolo";
import { sanitizePatternLoop, type PatternLoopRange } from "./patternLoop";
import { normalizeTrackColors, type TrackColor } from "./trackColors";
import { normalizeAdamBindings } from "../controls/controlPrefs";
import type { AdamControlBinding } from "../controls/controlRouter";

export const PROJECT_TYPE = "adam-project" as const;
/** Legacy pre-Adam “Audio Lab” export tag — still accepted by {@link parseLabProject}. */
export const LEGACY_PROJECT_TYPE = "curva-audio-lab-project" as const;
export const PROJECT_VERSION = 1 as const;

export function isLabProjectType(type: unknown): type is typeof PROJECT_TYPE | typeof LEGACY_PROJECT_TYPE {
  return type === PROJECT_TYPE || type === LEGACY_PROJECT_TYPE;
}

export type LabProjectArrangement = {
  scenes: Scene[];
  order: string[];
};

export type LabProjectFile = {
  type: typeof PROJECT_TYPE;
  version: typeof PROJECT_VERSION;
  /** Pattern as hybrid DSL. */
  pattern: string;
  /** Scenes + song order (optional for legacy projects). */
  arrangement?: LabProjectArrangement;
  /** Preferred per-slot instruments (synth | drums). */
  instruments?: Instrument[];
  /** Synth presets for slots (synth mirror / legacy). */
  presets: SynthPreset[];
  /** Named preset bank (slot defaults + keygen pack first). */
  bank: NamedPreset[];
  /** Named drum-kit bank. */
  drumBank?: NamedDrumKit[];
  /** Named wavetable bank. */
  wavetableBank?: NamedWavetablePreset[];
  /** Named sampler bank (sample ids + settings). */
  samplerBank?: NamedSamplerPreset[];
  /** Per-slot volume + pan. */
  slotMix?: SlotMix[];
  /** Per-slot insert FX stacks. */
  slotFx?: FxNode[][];
  /** Tracker mute flags. */
  muted?: boolean[];
  /** Tracker solo flags. */
  solo?: boolean[];
  /** Project-global track tints (ROW_MARKER_COLORS index, or null). */
  trackColors?: TrackColor[];
  /** Master post-FX stack. */
  fx: FxNode[];
  /** Loop brace — contiguous arrange range (pattern indices). */
  patternLoop?: PatternLoopRange;
  /**
   * MIDI / control learn bindings for this song.
   * Device/port prefs stay in localStorage — not here.
   */
  controlBindings?: AdamControlBinding[];
};

export type LabProjectSnapshot = {
  clip: ClipIR;
  instruments: Instrument[];
  presets: SynthPreset[];
  bank: NamedPreset[];
  drumBank: NamedDrumKit[];
  wavetableBank?: NamedWavetablePreset[];
  samplerBank?: NamedSamplerPreset[];
  /** Optional — omitted in older call sites → unity / center. */
  slotMix?: SlotMix[];
  /** Optional — omitted in older files → empty stacks. */
  slotFx?: FxNode[][];
  /** Optional — omitted in older files → all audible. */
  muted?: boolean[];
  /** Optional — omitted in older files → none soloed. */
  solo?: boolean[];
  /** Optional — omitted in older files → no tints. */
  trackColors?: TrackColor[];
  fx: FxNode[];
  /** Optional — omitted in older files → single active pattern. */
  patternLoop?: PatternLoopRange;
  /** Optional — omitted in older files → no learned bindings. */
  controlBindings?: AdamControlBinding[];
};

function sanitizeArrangement(raw: unknown): LabProjectArrangement | undefined {
  if (!raw || typeof raw !== "object") {
    return undefined;
  }
  const obj = raw as Record<string, unknown>;
  if (!Array.isArray(obj.scenes) || obj.scenes.length === 0) {
    return undefined;
  }
  const scenes: Scene[] = [];
  for (const entry of obj.scenes) {
    if (!entry || typeof entry !== "object") {
      continue;
    }
    const scene = entry as Record<string, unknown>;
    if (typeof scene.id !== "string" || !scene.id.trim()) {
      continue;
    }
    const order = Array.isArray(scene.order)
      ? scene.order.filter((id): id is string => typeof id === "string")
      : [];
    scenes.push({
      id: scene.id.trim(),
      ...(typeof scene.name === "string" && scene.name.trim()
        ? { name: scene.name.trim().slice(0, 48) }
        : {}),
      order,
    });
  }
  if (scenes.length === 0) {
    return undefined;
  }
  const order = Array.isArray(obj.order)
    ? obj.order.filter((id): id is string => typeof id === "string")
    : scenes.map(scene => scene.id);
  return { scenes, order };
}

function resolveProjectInstruments(obj: Record<string, unknown>, count: number): Instrument[] {
  const n = Math.min(MAX_INSTRUMENT_SLOT_COUNT, Math.max(1, Math.round(count)));
  if (Array.isArray(obj.instruments) && obj.instruments.some(entry => entry != null)) {
    return Array.from({ length: n }, (_, i) =>
      normalizeInstrument(obj.instruments![i], i as InstrumentSlot),
    );
  }
  const presets = Array.from({ length: n }, (_, i) => {
    const raw = Array.isArray(obj.presets) ? obj.presets[i] : undefined;
    if (raw && isSynthPreset(raw)) {
      return clonePreset(raw);
    }
    return defaultPresetForSlot(i);
  });
  return instrumentsFromPresets(presets, n);
}

export function buildLabProject(snapshot: LabProjectSnapshot): LabProjectFile {
  normalizeClipScenes(snapshot.clip);
  const instruments = (snapshot.instruments ?? instrumentsFromPresets(snapshot.presets))
    .map(cloneInstrument);
  const trackCount = instruments.length || DEFAULT_INSTRUMENT_SLOT_COUNT;
  ensureClipTrackCount(snapshot.clip, trackCount);
  return {
    type: PROJECT_TYPE,
    version: PROJECT_VERSION,
    pattern: stringifyPattern(snapshot.clip),
    arrangement: clipArrangementSnapshot(snapshot.clip),
    instruments,
    presets: presetsFromInstruments(instruments),
    bank: sanitizeBank(snapshot.bank),
    drumBank: sanitizeDrumBank(snapshot.drumBank ?? createDefaultDrumBank()),
    wavetableBank: sanitizeWavetableBank(snapshot.wavetableBank ?? createDefaultWavetableBank()),
    samplerBank: sanitizeSamplerBank(snapshot.samplerBank ?? createDefaultSamplerBank()),
    slotMix: normalizeSlotMixes(snapshot.slotMix, trackCount),
    slotFx: normalizeSlotFxStacks(snapshot.slotFx, trackCount),
    muted: normalizeMuteSoloFlags(snapshot.muted, trackCount),
    solo: normalizeMuteSoloFlags(snapshot.solo, trackCount),
    trackColors: normalizeTrackColors(snapshot.trackColors, trackCount),
    fx: snapshot.fx.map(fx => normalizeFxNode(fx)),
    patternLoop: sanitizePatternLoop(snapshot.patternLoop, snapshot.clip.patterns.length),
    ...(snapshot.controlBindings !== undefined
      ? { controlBindings: normalizeAdamBindings(snapshot.controlBindings) }
      : {}),
  };
}

export function parseLabProject(data: unknown): LabProjectSnapshot {
  if (!data || typeof data !== "object") {
    throw new Error("Invalid project JSON");
  }
  const obj = data as Record<string, unknown>;
  if (!isLabProjectType(obj.type)) {
    throw new Error(`Expected ${PROJECT_TYPE} (or legacy ${LEGACY_PROJECT_TYPE}), got ${String(obj.type)}`);
  }
  if (obj.version !== PROJECT_VERSION) {
    throw new Error(`Unsupported project version: ${String(obj.version)}`);
  }
  if (typeof obj.pattern !== "string") {
    throw new Error("Project missing pattern string");
  }

  const { clip, warnings } = parsePatternString(obj.pattern);
  if (warnings.length > 0 && clip.patterns.length === 0) {
    throw new Error(`Pattern parse failed: ${warnings[0]}`);
  }
  applyClipArrangement(clip, sanitizeArrangement(obj.arrangement));

  const trackCount = inferTrackCount({
    instruments: obj.instruments,
    presets: obj.presets,
    muted: obj.muted,
    solo: obj.solo,
    slotMix: obj.slotMix,
    slotFx: obj.slotFx,
    patternTrackCount: Math.max(0, ...clip.patterns.map(p => p.tracks.length)),
  });
  ensureClipTrackCount(clip, trackCount);

  const instruments = resolveProjectInstruments(obj, trackCount);
  const fx = Array.isArray(obj.fx)
    ? obj.fx
      .filter(node => node && typeof node === "object" && typeof (node as FxNode).type === "string")
      .map(node => normalizeFxNode(node as FxNode))
    : [];

  return {
    clip,
    instruments,
    presets: presetsFromInstruments(instruments),
    bank: sanitizeBank(obj.bank),
    drumBank: sanitizeDrumBank(obj.drumBank),
    wavetableBank: sanitizeWavetableBank(obj.wavetableBank),
    samplerBank: sanitizeSamplerBank(obj.samplerBank),
    slotMix: normalizeSlotMixes(obj.slotMix, trackCount),
    slotFx: normalizeSlotFxStacks(obj.slotFx, trackCount),
    muted: normalizeMuteSoloFlags(obj.muted, trackCount),
    solo: normalizeMuteSoloFlags(obj.solo, trackCount),
    trackColors: normalizeTrackColors(obj.trackColors, trackCount),
    fx,
    patternLoop: sanitizePatternLoop(obj.patternLoop, clip.patterns.length),
    controlBindings: normalizeAdamBindings(obj.controlBindings),
  };
}

export function downloadJson(text: string, filename: string): void {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type: "application/json" }));
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function downloadBytes(
  bytes: Uint8Array,
  filename: string,
  mimeType: string,
): void {
  const a = document.createElement("a");
  // Copy into a fresh ArrayBuffer-backed view — Blob rejects SharedArrayBuffer views.
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  a.href = URL.createObjectURL(new Blob([copy], { type: mimeType }));
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function downloadMidi(bytes: Uint8Array, filename: string): void {
  downloadBytes(bytes, filename, "audio/midi");
}

export function pickJsonFile(): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json,.json";
    input.addEventListener("change", async () => {
      const file = input.files?.[0];
      if (!file) {
        reject(new Error("No file selected"));
        return;
      }
      try {
        resolve(JSON.parse(await file.text()) as unknown);
      } catch (error) {
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
    input.click();
  });
}

/**
 * Pick an Adam project: `.adam` zip (preferred) or legacy `.json` project.
 * Returns raw bytes; caller decides zip vs JSON via {@link looksLikeZip}.
 */
export function pickAdamProjectFile(): Promise<{ bytes: Uint8Array; name: string }> {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".adam,application/vnd.adam+zip,application/zip,.json,application/json";
    input.addEventListener("change", async () => {
      const file = input.files?.[0];
      if (!file) {
        reject(new Error("No file selected"));
        return;
      }
      try {
        const buffer = await file.arrayBuffer();
        resolve({ bytes: new Uint8Array(buffer), name: file.name });
      } catch (error) {
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
    input.click();
  });
}

/** Pick a Standard MIDI File (.mid / .midi). */
export function pickMidiFile(): Promise<{ buffer: ArrayBuffer; name: string }> {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "audio/midi,audio/x-midi,.mid,.midi";
    input.addEventListener("change", async () => {
      const file = input.files?.[0];
      if (!file) {
        reject(new Error("No file selected"));
        return;
      }
      try {
        resolve({ buffer: await file.arrayBuffer(), name: file.name });
      } catch (error) {
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
    input.click();
  });
}
